import React, { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'
import { Typography, Button, Box, Spinner, FlexBox, Icon } from '@vp/swan'
import Parser from 'html-react-parser'
import { withTranslation } from 'react-i18next'
// Components
import styles, { stylesheet } from './Email.scss'
import EmailModal from './EmailModal'
import EmailPhoneCtaModal from './EmailPhoneCtaModal'
// Services
import CallMeService from '../../services/callMe/callMeService'
import AnalyticsTracking from '../analytics/analytics'
import { AssetStoreProvider } from '../../contexts/AssetStoreProvider'
import useStatusChecker from '../../hooks/useStatusChecker'
import { useBannerStatusContext } from '../../contexts/StatusContext'
import { DisabledEmail } from './DisabledEmail'
import { useStyles } from '@vp/ubik-context'
import { useConfig } from '../../contexts/ConfigProvider'
import { useIdentity } from '@vp/ubik-context'
export const createAuthConfig = (culture, isDevelopment) => ({
  culture,
  developmentMode: isDevelopment,
  options: {
    requireSession: true,
    customText: 'Sign in to see your account',
  },
})
const Email = (props) => {
  const { t, contactChannels, locale, tenant, proadvantageType, isCallMeEnabled } = props
  const lng = locale.toLowerCase().replace('-', '_')
  const isProadvantage = proadvantageType !== ''
  const { initialData } = useBannerStatusContext()
  const { data: status, isLoading: loadingStatus } = useStatusChecker(props.tenant, props.locale, initialData)
  const config = useConfig()
  const auth = useIdentity()
  useStyles(stylesheet)
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  const [callMeResponse, setCallMeResponse] = useState({})
  const [hasError, setHasError] = React.useState(false)

  const handleError = () => {
    setHasError(true)
  }
  const callMeService =
    callMeResponse?.status && callMeResponse?.businessHours && callMeResponse?.status?.isScheduleOpen && callMeResponse.status.isFeatureEnabled
  const c2cDisabled = status?.enabled && status?.config?.c2cDisabled
  useEffect(() => {
    if (!isCallMeEnabled) {
      return
    }
    const callMeService = new CallMeService()
    async function fetchCallMeService() {
      const response = await callMeService.getCallMeSchedule(tenant, locale, 'Regular', config)
      if (response?.status && response?.businessHours && response?.businessHours?.timezone?.short_format) {
        setCallMeResponse(response)
      }
    }

    fetchCallMeService()
  }, [])

  const closeModal = () => {
    setIsModalOpen(false)
  }

  let scheduleMsg = contactChannels.email.scheduleMsg
  if (typeof window !== 'undefined') {
    scheduleMsg = DOMPurify.sanitize(contactChannels.email.scheduleMsg)
  }

  let proadvantageContactPhrase = t(`${tenant}:email.proadvantage.call_us`, { lng }, '')
  const proadvantageEmailAddress =
    proadvantageType === 'basic' ? contactChannels.email.proadvantageEmailAddress : contactChannels.email.premiumProadvantageEmailAddress
  const proadvantageEmailText = `<a href='mailto:${proadvantageEmailAddress}'>${proadvantageEmailAddress}</a>`

  proadvantageContactPhrase = proadvantageContactPhrase.split('{1}').join(proadvantageEmailText)
  const proadvantageSupportPhrase = t(`${tenant}:email.proadvantage.support_care_team`, { lng }, '')

  const initiateEmailForm = () => {
    setIsModalOpen(true)
    AnalyticsTracking.trackEventWithDetails('Pop-Up Viewed', {
      category: 'Pop-Up',
      label: 'Email Initiated',
      pageSection: 'Help Center',
      pageStage: 'Customer Care',
      pageName: 'Home Page:Help Center',
      eventDetail: callMeService ? 'BH;' : ' ',
    })
  }

  if (loadingStatus) {
    return (
      <Spinner
        mt={{ lg: '7', md: '7', sm: '6', xs: '6' }}
        style={{ width: '100%' }}
        accessibleText={t(`${tenant}:search_results.preloader_accessibility_label`, { lng })}
        size="standard"
      />
    )
  }

  if (status?.enabled && status?.config?.emailDisabled) return <DisabledEmail {...props} />
  return (
    <Box className={styles.emailFormContainer}>
      <AssetStoreProvider>
        <Box paddingRight="2" className={styles.contentContainer}>
          <FlexBox flexDirection="column">
            <Box>
              <div style={{ display: 'flex' }}>
                <Typography fontSkin="title-subsection" mb={4}>
                  {t(`${tenant}:email.title`, { lng })}
                </Typography>
              </div>
            </Box>
            <Box>
              {!isProadvantage ? (
                <Typography fontSize="standard" mb={0} mt={0} dangerouslySetInnerHTML={{ __html: scheduleMsg }} />
              ) : (
                <Typography fontWeight="bold" fontSize="standard" mb={0} mt={0}>
                  {Parser(proadvantageContactPhrase)}
                </Typography>
              )}
            </Box>
          </FlexBox>
          <Box pt="5">
            {!isProadvantage ? (
              <Button
                iconPosition="left"
                style={{ cursor: 'pointer' }}
                onClick={initiateEmailForm}
                skin="secondary"
                type="button"
                name="show_email_form"
                value="show_email_form"
              >
                <FlexBox>
                  <Icon iconType="email" mr={3} />
                  {t(`${tenant}:email.buttons.send_email.title`, { lng })}
                </FlexBox>
              </Button>
            ) : (
              <Typography fontSize="standard" mb={0}>
                {proadvantageSupportPhrase}
              </Typography>
            )}
          </Box>
        </Box>

        {!isProadvantage ? (
          callMeService && !hasError && locale.toLowerCase() === 'en-us' && !c2cDisabled ? (
            <EmailPhoneCtaModal
              tenant={tenant}
              locale={locale}
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              lng={lng}
              contactChannels={contactChannels}
              proadvantageType={proadvantageType}
              handleError={handleError}
              callMeService={callMeService}
              auth={auth}
            />
          ) : (
            <EmailModal
              tenant={tenant}
              locale={locale}
              contactChannels={contactChannels}
              proadvantageType={proadvantageType}
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              dataConfig={config}
              auth={auth}
            />
          )
        ) : null}
      </AssetStoreProvider>
    </Box>
  )
}

export default withTranslation()(Email)
